import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useReadProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import { Button } from "@/components/ui/button";
import { Option } from "@/types/Shared/select";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import ControlledAutoComplete from "@/pages/Project/Detail/components/ControlledAutoComplete";
import {
  useInviteProjectGroupsTasksAssignees,
  useReadProjectGroupsOneTask,
} from "@/libs/react-query/hooks/useTaskQuery";
import { CurrentUserContent } from "@/types/setting";
import useUserStore from "@/stores/User";

const formSchema = z.object({
  assignees: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      })
    )
    .min(1, {
      message: "참여자를 추가해주세요.",
    }),
});

type FormSchema = z.infer<typeof formSchema>;

export default function InviteUserDialog() {
  const [open, setOpen] = useState(false);
  const [searchName, setSearchName] = useState("");

  const storedUser: CurrentUserContent = useUserStore((state) => state.user);

  const { task_id = "", group_id = "" } = useParams();

  const { data: { users = [] } = {}, isError: isErrorUsers } =
    useReadProjectGroups(group_id);
  const { data: { assignees = [] } = {}, isError: isErrorAssignees } =
    useReadProjectGroupsOneTask(group_id, task_id);
  const { mutate: inviteAssignees } = useInviteProjectGroupsTasksAssignees({
    group_id,
    task_id,
  });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      assignees: [],
    },
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = form;

  const handleInviteUser = (data: FormSchema) => {
    inviteAssignees(
      {
        assignees: data.assignees.map((user) => ({ user_id: user.value })),
      },
      {
        onSuccess: () => handleOpenChange(false),
      }
    );
  };

  const commandOptions: Option[] = useMemo(() => {
    if (!users || !assignees) return [];

    return users
      .filter(
        (user) =>
          !assignees.some((a) => a.user_id === user.user_id) &&
          user.user_id !== storedUser.user_id
      )
      .map((user) => ({
        value: user.user_id,
        label: user.user_name,
        ...user,
      }));
  }, [users, assignees]);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      setOpen(value);
      if (!value) {
        reset();
        setSearchName("");
      }
    },
    [reset]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <div className="flex items-center gap-2">
        <Avatar className="size-[24px]">
          <AvatarFallback></AvatarFallback>
        </Avatar>

        <DialogTrigger asChild>
          <div className="flex items-center gap-[2px] cursor-pointer">
            <span className="text-xs text-gray-B font-semibold">
              참여자 추가하기
            </span>
            <Plus size={16} className="text-gray-B" />
          </div>
        </DialogTrigger>
      </div>

      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <Form {...form}>
          <form onSubmit={handleSubmit(handleInviteUser)}>
            <DialogHeader>
              <DialogTitle>참여자 추가하기</DialogTitle>
              <DialogDescription />
            </DialogHeader>

            <div className="flex flex-col gap-2">
              <ControlledAutoComplete
                name="assignees"
                label="참여자 추가하기"
                placeholder="이름으로 검색해보세요"
                commands={commandOptions}
                inputValue={searchName}
                setInputValue={setSearchName}
              />
            </div>
            <div className="mt-4 flex justify-center space-x-4">
              <Button
                type="button"
                variant="cancel"
                onClick={() => handleOpenChange(false)}
              >
                취소하기
              </Button>
              <Button type="submit" variant="confirm" disabled={!isDirty}>
                추가하기
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
